import dayjs from "dayjs";
import { useTimeData } from "src/components/monitoring/senec-time-frame-selection/TimeFrameContext";
import { TimeFrame, TimeRange } from "src/components/monitoring/commonTypes";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    SenecFunctionalIconButton,
    SenecTimeFramePicker,
    SenecTimeIntervalDescriptor,
    SenecTimeIntervalSelection,
} from "src/newsenec-ui-components";
import { Stack } from "@mui/material";
import {
    getLatestTimeFrame,
    getNextTimeFrame,
    getPrevTimeFrame,
    getValidTimeFrame,
    isToday,
    isValidTimeFrame,
} from "src/components/monitoring/senec-time-frame-selection/timeFrameUtils";
import { useScreenSize } from "src/hooks/useScreenSize";
import { SenecButtonColorTheme } from "src/components/button/ButtonUtils";
import { DateOrTimeView } from "@mui/x-date-pickers/models";
import { ReactElement, useEffect } from "react";

export const SenecTimeFrameSelection = ({
    isLoading = false,
    timeIntervals,
    showSwitchers = true,
    buttonColorTheme = "light",
    views,
    inBetweenElements,
    keepTimeRange = false,
    disableFuture = true,
    offset = 0,
}: {
    isLoading?: boolean;
    timeIntervals?: SenecTimeIntervalDescriptor[];
    showSwitchers?: boolean;
    buttonColorTheme?: SenecButtonColorTheme;
    /** Optional Array to set "view" field of Mui DateTimePicker. Allows to add or omit any time frame selection field from picker e.g. views={["year", "month", "day", "hours"]} would omit minutes from selection. Seconds are omitted by default */
    views?: DateOrTimeView[];
    inBetweenElements?: ReactElement;
    /** If set to true, the time range will remain as it was, in case of previous or next switch press. @default is false */
    keepTimeRange?: boolean;
    /** If set to false, it will be possible to set end date from the future. @default is true */
    disableFuture?: boolean;
    /** In some cases it is necessary that date is shifted by some offset in case next or previous button is pressed. (e.g. 00:00 - 23:59 requires shifting date by one minute after next press). This sets the required offset in minutes. @default is 0 */
    offset?: number;
}) => {
    const { timeRange, timeFrame, onTimeFrameChange, onTimeRangeChange } = useTimeData();
    const { isMobile } = useScreenSize();

    useEffect(() => {
        if (disableFuture) {
            onTimeFrameChange(getValidTimeFrame(timeFrame, getLatestTimeFrame(timeRange, disableFuture)!));
        } else {
            if (timeRange !== TimeRange.CUSTOM && timeRange !== TimeRange.FREE && dayjs().diff(dayjs(timeFrame.end), "day") < 1) {
                onTimeFrameChange(getLatestTimeFrame(timeRange, disableFuture)!);
            }
        }
    }, [disableFuture]);

    return (
        <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={{ lg: "space-between", md: "space-between", xs: "center" }}
            spacing={{ lg: 3, md: 2, xs: 0 }}
            gap={3}
            flexWrap={"wrap"}
        >
            {timeIntervals && timeIntervals.length > 0 && (
                <Stack>
                    <Stack justifyContent={"center"} direction={"row"} flexWrap={"nowrap"} alignItems={"center"} gap={2}>
                        {isMobile && showSwitchers && (
                            <PreviousButton
                                isLoading={isLoading}
                                colorTheme={buttonColorTheme}
                                keepTimeRange={keepTimeRange}
                                offset={offset}
                            />
                        )}
                        <SenecTimeIntervalSelection
                            dataTestId={"senec-time-interval-selection"}
                            timeIntervals={timeIntervals}
                            selectedTimeInterval={timeRange}
                            onChangeTimeInterval={(value: string) => {
                                const newTimeRange = value as TimeRange;
                                if (newTimeRange !== TimeRange.FREE) {
                                    onTimeRangeChange(newTimeRange);
                                    if (newTimeRange !== TimeRange.CUSTOM) {
                                        onTimeFrameChange(getLatestTimeFrame(newTimeRange, disableFuture)!);
                                    }
                                }
                            }}
                        />
                        {isMobile && showSwitchers && (
                            <NextButton
                                isLoading={isLoading}
                                colorTheme={buttonColorTheme}
                                keepTimeRange={keepTimeRange}
                                disableFuture={disableFuture}
                                offset={offset}
                            />
                        )}
                    </Stack>
                </Stack>
            )}
            {inBetweenElements && inBetweenElements}
            <Stack>
                <Stack
                    data-testid="senec-section-switcher"
                    direction={"row"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    gap={3}
                    height={"32px"}
                >
                    {!isMobile && showSwitchers && (
                        <PreviousButton
                            isLoading={isLoading}
                            colorTheme={buttonColorTheme}
                            keepTimeRange={keepTimeRange}
                            offset={offset}
                        />
                    )}
                    <SenecTimeFramePicker
                        isLoading={isLoading}
                        views={views}
                        disableFuture={disableFuture}
                        onChange={() => {
                            onTimeRangeChange(TimeRange.CUSTOM);
                        }}
                    />
                    {!isMobile && showSwitchers && (
                        <NextButton
                            isLoading={isLoading}
                            colorTheme={buttonColorTheme}
                            keepTimeRange={keepTimeRange}
                            disableFuture={disableFuture}
                            offset={offset}
                        />
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

const PreviousButton = ({
    isLoading,
    colorTheme,
    offset,
    keepTimeRange,
}: {
    isLoading: boolean;
    colorTheme: SenecButtonColorTheme;
    offset: number;
    keepTimeRange?: boolean;
}) => {
    const { timeFrame, timeRange, labels, onTimeFrameChange, onTimeRangeChange } = useTimeData();

    const disablePreviousButton = timeRange === TimeRange.TODAY || timeRange === TimeRange.CUSTOM;

    return (
        <SenecFunctionalIconButton
            data-testid="previous-switcher"
            level="secondary"
            colorTheme={colorTheme}
            size="small"
            disabled={isLoading || disablePreviousButton}
            onClick={() =>
                handleSwitcherClick(
                    timeRange,
                    timeFrame,
                    onTimeFrameChange,
                    onTimeRangeChange,
                    offset,
                    false,
                    keepTimeRange,
                    true,
                )
            }
            aria-label={labels.previous}
        >
            <ChevronLeftIcon />
        </SenecFunctionalIconButton>
    );
};

const NextButton = ({
    isLoading,
    colorTheme,
    offset,
    keepTimeRange,
    disableFuture,
}: {
    isLoading: boolean;
    colorTheme: SenecButtonColorTheme;
    offset: number;
    keepTimeRange?: boolean;
    disableFuture?: boolean;
}) => {
    const { timeFrame, timeRange, labels, onTimeFrameChange, onTimeRangeChange } = useTimeData();

    const disablePreviousButton = timeRange === TimeRange.TODAY || timeRange === TimeRange.CUSTOM;

    return (
        <SenecFunctionalIconButton
            data-testid="next-switcher"
            level="secondary"
            colorTheme={colorTheme}
            size="small"
            disabled={
                isToday(dayjs(timeFrame.end)) ||
                !isValidTimeFrame(getNextTimeFrame(timeRange, timeFrame, offset, disableFuture), disableFuture) ||
                isLoading ||
                disablePreviousButton
            }
            onClick={() =>
                handleSwitcherClick(
                    timeRange,
                    timeFrame,
                    onTimeFrameChange,
                    onTimeRangeChange,
                    offset,
                    true,
                    keepTimeRange,
                    disableFuture,
                )
            }
            aria-label={labels.next}
        >
            <ChevronRightIcon />
        </SenecFunctionalIconButton>
    );
};

export const handleSwitcherClick = (
    timeRange: TimeRange,
    timeFrame: TimeFrame,
    onTimeFrameChange: (timeFrame: TimeFrame) => void,
    onTimeRangeChange: (timeRange: TimeRange) => void,
    offset: number,
    isSwitchNext = true,
    keepTimeRange = false,
    disableFuture = true,
) => {
    const switchTimeFrame = isSwitchNext
        ? getNextTimeFrame(timeRange, timeFrame, offset, disableFuture)
        : getPrevTimeFrame(timeRange, timeFrame, offset);
    if (isValidTimeFrame(switchTimeFrame, disableFuture)) {
        if (keepTimeRange) {
            onTimeRangeChange(timeRange);
        } else {
            onTimeRangeChange(TimeRange.FREE);
        }
        onTimeFrameChange(switchTimeFrame);
    }
};
